import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { TypographyPropAs } from './TypographyTypes';
import HTMLComment from 'react-html-comment';
import parse from 'html-react-parser';

const typographyVariants = cva('', {
    variants: {
        variant: {
            'headline-xl': [
                'text-balance font-normal',
                'text-17xl leading-4 -tracking-4',
                'md:text-20xl md:leading-3 md:-tracking-5',
                '2xl:text-21xl 2xl:leading-2',
                '3xl:text-22xl 3xl:-tracking-6',
                '4xl:text-23xl 4xl:leading-1',
            ],
            'headline-lg': [
                'text-balance font-normal',
                'text-12xl leading-4 -tracking-5',
                '2xl:text-15xl 2xl:leading-3 -tracking-7',
                '3xl:text-18xl',
                '4xl:text-19xl',
            ],
            'headline-md': [
                'text-balance font-normal',
                'text-8xl leading-5 -tracking-3',
                '2xl:text-12xl 2xl:-tracking-5',
                '3xl:text-14xl 3xl:leading-4',
                '4xl:text-16xl',
            ],
            'headline-sm': [
                'text-balance font-normal',
                'text-5xl leading-6',
                '2xl:text-10xl 2xl:leading-5 2xl:-tracking-3',
                '3xl:text-11xl 3xl:-tracking-4',
                '4xl:text-13xl',
            ],
            'headline-xs': ['text-balance font-normal', 'text-3xl leading-7', '2xl:text-6xl 2xl:-tracking-3', '3xl:text-7xl', '4xl:text-9xl 4xl:leading-6'],
            'copy': ['text-md leading-9', '2xl:text-xl', '3xl:text-2xl', '4xl:text-4xl'],
            'copy-medium': ['text-md leading-9 font-medium', '2xl:text-xl', '3xl:text-2xl', '4xl:text-4xl'],
            'copy-semibold': ['text-md leading-9 font-semibold', '2xl:text-xl', '3xl:text-2xl', '4xl:text-4xl'],
            'copy-small': ['text-xs leading-8 tracking-1', '2xl:text 2xl:leading-9', '3xl:text-lg 3xl:tracking', '4xl:text-xl'],
            'button': ['font-medium leading-3 tracking-3'],
            'textlink': ['text-sm leading-8 tracking-3'],
            'micro-copy': ['text-3xs leading-7 tracking-5'],
            'micro-copy-medium': ['font-medium', 'text-3xs leading-7 tracking-5'],
            'topline': ['uppercase', 'text-4xs leading-6 tracking-8'],
            'navigation-main': ['text-5xl leading-6 tracking-2'],
            'navigation-meta': ['text-2xs leading tracking-5'],
            'navigation-footer': ['leading-6'],
            'navigation-breadcrumb': ['text-2xs leading-6 tracking-5'],
        },
        hyphens: {
            auto: 'hyphens-auto',
            manual: 'hyphens-manual',
        },
    },
    defaultVariants: {
        variant: 'copy',
        hyphens: 'manual',
    },
});

export interface TypographyProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof typographyVariants> {
    as?: TypographyPropAs;
    className?: string;
    children?: React.ReactNode;
}

const Typography: React.FC<TypographyProps> = ({ variant, hyphens, className = '', as: AsTag = 'div', children = '', ...props }) => {
    return (
        <>
            <HTMLComment text={`@component: Typography - Start (variant: ${variant ? variant : 'copy'})`} />
            {className && <HTMLComment text={`Additional classes from component "${className}"`} />}
            <AsTag className={twMerge(typographyVariants({ variant, hyphens, className }))} {...props}>
                {typeof children === 'object' ? children : parse(children as string)}
            </AsTag>
            <HTMLComment text={'@component: Typography - End'} />
        </>
    );
};

export default Typography;
